// ABOUTME: Recognises the request timeouts the UI must not report as a plain error: the 60s
// ABOUTME: read-model wait (still processing) and Boomi's 504 on a certification (still certifying): both warnings.

import { formatGlobalMessage } from '@src/_new/utils/globalIntl';
import { defineMessage } from 'react-intl';

/** Extension code the server tags the read-model wait timeout with (waitProjectionUpdate). */
export const PROCESSING_TIMEOUT_ERROR_CODE = 'PROCESSING_TIMEOUT';

/** Message of the read-model wait timeout, for errors that reach the client as plain text. */
const PROCESSING_TIMEOUT_MESSAGE = 'Max wait time of 60s reached';

/** Status code a certification answers with when Boomi cannot handle the call within its time limit. */
export const GATEWAY_TIMEOUT_STATUS_CODE = 504;

/** GraphQL operation names of the certification mutations, the ones a 504 is a Boomi failure for. */
const CERTIFICATION_OPERATION_NAMES = ['CertificateNode', 'CertificatePrototype'];

/** Shown when the read model has not acknowledged the events within the 60s wait window. */
export const processingTimeoutMessage = defineMessage({
    id: 'processing-timeout.warning',
    defaultMessage:
        'Processing in progress: the process is taking longer than expected. Please wait',
});

/**
 * Shown when a certification answers 504: Boomi did not answer within the gateway time limit
 * (usually a very large payload) but keeps processing the call, so the user waits for the outcome.
 */
export const certificationTimeoutMessage = defineMessage({
    id: 'certification-timeout.warning',
    defaultMessage:
        'Certification in progress: the operation is taking longer than expected. Please wait for the outcome',
});

type GraphQLErrorLike = {
    message: string;
    extensions?: Record<string, unknown> | null;
};

/** True for the GraphQL error the server builds on the read-model wait timeout. */
export const isProcessingTimeoutGraphQLError = ({
    message,
    extensions,
}: GraphQLErrorLike): boolean =>
    extensions?.code === PROCESSING_TIMEOUT_ERROR_CODE ||
    message.includes(PROCESSING_TIMEOUT_MESSAGE);

/** True for a network error carrying the gateway timeout status. */
export const isGatewayTimeoutNetworkError = (networkError: unknown): boolean =>
    (networkError as { statusCode?: number } | null)?.statusCode === GATEWAY_TIMEOUT_STATUS_CODE;

/** Same, for the ApolloError a mutation rejects with. */
export const isGatewayTimeoutError = (error: unknown): boolean =>
    isGatewayTimeoutNetworkError((error as { networkError?: unknown } | null)?.networkError);

/** True when the operation is one of the certification mutations. */
export const isCertificationOperation = (operationName: string | undefined | null): boolean =>
    !!operationName && CERTIFICATION_OPERATION_NAMES.includes(operationName);

/** The translated "still processing" text for the read-model wait timeout. */
export const getProcessingTimeoutMessage = (): string =>
    formatGlobalMessage(processingTimeoutMessage);

/** The translated "still certifying" text for a certification that timed out on the Boomi side. */
export const getCertificationTimeoutMessage = (): string =>
    formatGlobalMessage(certificationTimeoutMessage);

/**
 * Maps the raw message of the read-model wait timeout (call sites usually show `error.message`)
 * to the translated warning to display instead; null for any other message.
 */
export const getTimeoutWarningForMessage = (message: string | undefined | null): string | null =>
    message && message.includes(PROCESSING_TIMEOUT_MESSAGE) ? getProcessingTimeoutMessage() : null;
